import React, { useEffect } from "react";
import BackHeader from "@components/header/BackHeader";
import { FormProvider, useForm } from "react-hook-form";
import styled from "@emotion/styled";
import ProgressBar from "@ramonak/react-progress-bar";
import { useTheme } from "@emotion/react";
import useRegisterStepStore from "@store/register/useRegisterStepStore";
import {
  RegisterProductStepFour,
  RegisterProductStepOne,
  RegisterProductStepThree,
  RegisterProductStepTwo,
} from "@pages/register/_components";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { productSchema } from "@libs/zod/zodVaildation";
import { useApiRequest } from "@utils/ApiRequest";
import { useQuery } from "@tanstack/react-query";
import useAxios from "@libs/axios/useAxios";
import { POST_PRODUCT_URL } from "@libs/apiUrl";
import { queryClient } from "@libs/reactQuery/queryClient";
import Image from "@components/item/Image";
import { useTranslation } from "react-i18next";

const FormContainer = styled.form({
  position: "relative",
  display: "flex",
  flexDirection: "column",
});

const ContentWrapper = styled.div(({ theme }) => ({
  padding: "3rem 2rem 0",
}));

const CloseIconBox = styled.button({
  position: "absolute",
  right: "1.6rem",
});

const RegisterProduct = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { step, decreaseStep, resetStep } = useRegisterStepStore();
  const { getUserInfo } = useApiRequest();
  const { axiosInstance } = useAxios();

  const { data: userInfo, isLoading: userInfoLoading } = useQuery({
    queryKey: ["userInfo"],
    queryFn: getUserInfo,
  });

  const stepArr = [
    {
      key: 1,
      component: <RegisterProductStepOne />,
    },
    { key: 2, component: <RegisterProductStepTwo /> },
    { key: 3, component: <RegisterProductStepThree /> },
    { key: 4, component: <RegisterProductStepFour /> },
  ];
  const { t } = useTranslation();

  const method = useForm({
    resolver: zodResolver(productSchema(t)),
    mode: "onChange",
    reValidateMode: "onChange",

    defaultValues: {
      brand_id: null,
      brand_name: "",
      size_type_id: null,

      depth1_size_type_id: null,
      depth2_size_type_id: null,
      depth3_size_type_id: null,
      depth4_size_type_id: null,

      name_ko: "",
      name_en: "",
      depth1: null,
      depth2: null,
      depth3: null,
      depth4: null,
      product_condition: "",
      stock_list: [],
      is_damaged: 0,

      image_list: [],
      thumbnail_url: "",
      price: 0,
      description_ko: "",
      description_en: "",
      terms: false,
    },
  });

  const { handleSubmit, watch, formState, reset } = method;

  const handleBackEvent = () => {
    step === 1 ? navigate("/main-register") : decreaseStep();
  };

  const onSubmit = async (data: any) => {
    const category_id =
      data.depth4 || data.depth3 || data.depth2 || data.depth1;

    const postStockList = data.stock_list.map((stock: any) => ({
      color_id: stock.color_id, // 색상 ID
      size_list: stock.size_list.map((size: any) => ({
        size_id: size.size_id, // 사이즈 ID
        amount: size.amount, // 해당 사이즈의 수량
      })),
    }));

    let postData = {
      seller_id: userInfo.id,
      brand_id: data.brand_id,
      category_id: category_id,
      name_ko: data.name_ko,
      name_en: data.name_en,
      is_damaged: data.is_damaged,
      product_condition: data.product_condition,
      thumbnail_url: data.thumbnail_url,
      price: data.price,
      description_ko: data.description_ko,
      description_en: data.description_en,
      image_list: data.image_list,
      stock_list: postStockList,
    };

    const res = await axiosInstance.post(POST_PRODUCT_URL, postData);

    if (res.status === 200) {
      queryClient.resetQueries({
        queryKey: ["productList"],
      });
      resetStep();
      navigate("/success-register", {
        state: { productId: res.data.data.product_id },
        replace: true,
      });
    }
  };

  const handleClose = () => {
    resetStep();
    navigate("/main-register");
  };

  useEffect(() => {
    reset();
  }, []);

  return userInfoLoading ? (
    <></>
  ) : (
    <FormProvider {...method}>
      <FormContainer onSubmit={handleSubmit(onSubmit)}>
        <BackHeader title="상품등록" onClick={handleBackEvent}>
          <CloseIconBox onClick={handleClose} type="button">
            <Image src={theme.image.close.default} width="2rem" height="2rem" />
          </CloseIconBox>
        </BackHeader>

        <ProgressBar
          completed={step}
          maxCompleted={4}
          isLabelVisible={false}
          borderRadius={"0"}
          height="5px"
          bgColor={theme.color.mint}
          baseBgColor={theme.color.border_gray}
        />
        <ContentWrapper>{stepArr[step - 1].component}</ContentWrapper>
      </FormContainer>
    </FormProvider>
  );
};

export default RegisterProduct;
